import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { resolveTheme, STATUS_COLORS, NEUTRAL, onAccent } from './designTokens';
import { darkPrimary } from './components/primitives/DarkHeader';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';
import { validateImageFile, loadImageFile, hasTransparency, makeLightVersion, makeSquareIcon, uploadBrandImage, fileExt } from './brandImages';

const PRESET_COLORS = [
  '#2F6FDE', '#2C513F', '#E87A24', '#7C3AED', '#0F766E',
  '#B91C1C', '#DB2777', '#0891B2', '#475569', '#0F172A',
];

const ICONS = {
  up: <><path d="M12 16V4M7 9l5-5 5 5" /><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></>,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
};
const Icon = ({ name, color, size = 18, sw = 2 }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{ICONS[name]}</svg>
);

function isValidHex(val) {
  return /^#[0-9A-Fa-f]{6}$/.test((val || '').trim());
}

const smallBtn = { minHeight: '40px', padding: '0 14px', borderRadius: '9px', border: `1px solid ${NEUTRAL.line}`, background: '#FFFFFF', color: NEUTRAL.ink, fontWeight: '600', fontSize: '13px', fontFamily: 'inherit', cursor: 'pointer' };

// T-14: en enda uppladdning. Genomskinlig bild → ljus version skapas automatiskt för mörka ytor.
// Bild med egen bakgrund → visas på vit platta på mörka ytor. Förhandsvisningen visar exakt det kunden ser.
function LogoSection({ logoUrl, logoDarkUrl, onChange, therapistId, theme }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [justAuto, setJustAuto] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const darkRef = useRef(null);

  const handleLogo = async (file) => {
    setError(null); setJustAuto(false);
    const invalid = validateImageFile(file);
    if (invalid) { setError(invalid); return; }
    setBusy(true);
    try {
      const img = await loadImageFile(file);
      const url = await uploadBrandImage(therapistId, file, 'logga', fileExt(file));
      let dark = '';
      if (hasTransparency(img)) {
        const blob = await makeLightVersion(img);
        dark = await uploadBrandImage(therapistId, blob, 'logga-ljus', 'png');
        setJustAuto(true);
      }
      onChange({ logo_url: url, logo_dark_url: dark });
    } catch (e) {
      setError(e.message || 'Något gick fel. Försök igen.');
    }
    setBusy(false);
  };

  const handleOwnDark = async (file) => {
    setError(null); setJustAuto(false);
    const invalid = validateImageFile(file);
    if (invalid) { setError(invalid); return; }
    setBusy(true);
    try {
      const url = await uploadBrandImage(therapistId, file, 'logga-ljus-egen', fileExt(file));
      onChange({ logo_url: logoUrl, logo_dark_url: url });
    } catch (e) {
      setError(e.message || 'Något gick fel. Försök igen.');
    }
    setBusy(false);
  };

  const pick = (ref) => ref.current?.click();
  const onInput = (handler) => (e) => { const f = e.target.files?.[0]; if (f) handler(f); e.target.value = ''; };

  return (
    <div>
      <div style={{ fontSize: '14px', fontWeight: '700', color: NEUTRAL.ink }}>Din logga</div>
      <div style={{ fontSize: '13px', color: NEUTRAL.muted, marginTop: '2px', marginBottom: '12px', lineHeight: 1.5 }}>Visas för dina kunder i appen och på inloggningssidan.</div>

      {!logoUrl ? (
        <button type="button" onClick={() => pick(fileRef)} disabled={busy}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) handleLogo(f); }}
          style={{ width: '100%', border: `2px dashed ${dragOver ? theme.accent : '#CBD5E1'}`, borderRadius: '14px', background: NEUTRAL.page, padding: '36px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', fontFamily: 'inherit', cursor: busy ? 'default' : 'pointer' }}>
          <span style={{ width: '52px', height: '52px', borderRadius: '14px', background: theme.accent_soft_bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="up" color={theme.accent_soft_text} size={24} /></span>
          <span style={{ fontSize: '16px', fontWeight: '700', color: NEUTRAL.ink }}>{busy ? 'Laddar upp…' : 'Ladda upp din logga'}</span>
          {!busy && <span style={{ fontSize: '13px', color: NEUTRAL.muted }}>Klicka här eller dra filen hit</span>}
          {!busy && <span style={{ fontSize: '12px', color: NEUTRAL.muted, marginTop: '4px' }}>Bäst: PNG med genomskinlig bakgrund · JPG går också bra</span>}
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Exakt det kunden ser: mörk yta, ljus logga direkt eller vanliga loggan på vit platta */}
          <div style={{ height: '140px', borderRadius: '12px', background: NEUTRAL.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box' }}>
            {logoDarkUrl ? (
              <img src={logoDarkUrl} alt="Din logga som kunden ser den" style={{ maxWidth: '240px', maxHeight: '90px', display: 'block' }} />
            ) : (
              <div style={{ background: '#FFFFFF', borderRadius: '12px', padding: '12px 18px' }}>
                <img src={logoUrl} alt="Din logga som kunden ser den" style={{ maxWidth: '200px', maxHeight: '64px', display: 'block' }} />
              </div>
            )}
          </div>
          <div style={{ fontSize: '12px', color: NEUTRAL.muted, marginTop: '-6px', textAlign: 'center' }}>Så ser dina kunder loggan</div>

          {logoDarkUrl ? (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', background: '#ECFDF3', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#166534', lineHeight: 1.5 }}>
              <span style={{ flexShrink: 0, marginTop: '1px' }}><Icon name="check" color="#16A34A" sw={2.4} /></span>
              <span><b>Klart!</b> {justAuto ? 'Vi gjorde en ljus version automatiskt så att loggan syns på mörka ytor.' : 'Loggan syns tydligt för dina kunder.'}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', background: '#EFF6FF', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#1E3A8A', lineHeight: 1.5 }}>
              <span style={{ flexShrink: 0, marginTop: '1px' }}><Icon name="info" color="#2563EB" sw={2.2} /></span>
              <span>Din bild har en egen bakgrund, så den visas på en vit platta. Det fungerar bra. Vill du slippa plattan kan du ladda upp en PNG med genomskinlig bakgrund.</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" style={smallBtn} onClick={() => pick(fileRef)} disabled={busy}>{busy ? 'Laddar upp…' : 'Byt logga'}</button>
            <button type="button" style={smallBtn} onClick={() => { setJustAuto(false); onChange({ logo_url: '', logo_dark_url: '' }); }} disabled={busy}>Ta bort</button>
            {logoDarkUrl && (
              <button type="button" onClick={() => pick(darkRef)} disabled={busy} style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: '6px 0', fontSize: '12px', color: NEUTRAL.muted, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' }}>
                Ser loggan fel ut? Ladda upp en egen ljus version
              </button>
            )}
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onInput(handleLogo)} style={{ display: 'none' }} />
      <input ref={darkRef} type="file" accept="image/png,image/svg+xml,image/webp" onChange={onInput(handleOwnDark)} style={{ display: 'none' }} />
      {error && <div role="alert" style={{ marginTop: '10px', fontSize: '13px', color: STATUS_COLORS.error }}>{error}</div>}
    </div>
  );
}

// Appikon: valfri. Uppladdad bild beskärs automatiskt till kvadrat. Utan ikon: bokstav i klinikens färg.
function IconSection({ iconUrl, onChange, therapistId, theme, name }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const letter = (name || 'K').trim().charAt(0).toUpperCase();

  const handle = async (file) => {
    setError(null);
    const invalid = validateImageFile(file);
    if (invalid) { setError(invalid); return; }
    setBusy(true);
    try {
      const img = await loadImageFile(file);
      const blob = await makeSquareIcon(img);
      onChange(await uploadBrandImage(therapistId, blob, 'ikon', 'png'));
    } catch (e) {
      setError(e.message || 'Något gick fel. Försök igen.');
    }
    setBusy(false);
  };

  return (
    <div style={{ display: 'flex', gap: '18px', alignItems: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
        {iconUrl ? (
          <img src={iconUrl} alt="Din appikon" style={{ width: '64px', height: '64px', borderRadius: '16px', objectFit: 'cover', display: 'block', border: `1px solid ${NEUTRAL.line}` }} />
        ) : (
          <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: theme.accent, color: onAccent(theme.accent), display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontWeight: '800', fontSize: '28px' }}>{letter}</div>
        )}
        <span style={{ fontSize: '11px', color: NEUTRAL.muted, maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || 'Din klinik'}</span>
      </div>
      <div style={{ flex: 1, fontSize: '13px', color: NEUTRAL.text, lineHeight: 1.55 }}>
        {iconUrl
          ? 'Så här ser ikonen ut på kundens hemskärm.'
          : 'Har du ingen egen ikon används första bokstaven i klinikens namn i din färg, som här. Laddar du upp en bild beskär vi den till en kvadrat åt dig.'}
        <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
          <button type="button" style={smallBtn} onClick={() => fileRef.current?.click()} disabled={busy}>{busy ? 'Laddar upp…' : iconUrl ? 'Byt ikon' : 'Ladda upp egen ikon'}</button>
          {iconUrl && <button type="button" style={smallBtn} onClick={() => onChange('')} disabled={busy}>Ta bort</button>}
        </div>
        {error && <div role="alert" style={{ marginTop: '8px', fontSize: '13px', color: STATUS_COLORS.error }}>{error}</div>}
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={e => { const f = e.target.files?.[0]; if (f) handle(f); e.target.value = ''; }} style={{ display: 'none' }} />
    </div>
  );
}

function BrandAdmin({ therapist, onBrandSaved }) {
  const [form, setForm] = useState({
    display_name: '', logo_url: '', logo_dark_url: '', icon_url: '', tagline: '', accent_color: '',
    booking_confirmation_text: '', cancellation_info: '', reply_to_email: '', privacy_policy_url: '', custom_domain: '',
  });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [hasExisting, setHasExisting] = useState(false);
  const colorInputRef = useRef(null);

  const theme = resolveTheme(isValidHex(form.accent_color) ? form.accent_color.trim() : KLINIO_DEFAULT_ACCENT);

  useEffect(() => {
    if (!therapist?.id) return;
    fetchBrand();
  }, [therapist]);

  const fetchBrand = async () => {
    const { data } = await supabase
      .from('brand_settings')
      .select('*')
      .eq('therapist_id', therapist.id)
      .single();
    if (data) {
      setHasExisting(true);
      setForm({
        display_name: data.display_name || '',
        logo_url: data.logo_url || '',
        logo_dark_url: data.logo_dark_url || '',
        icon_url: data.icon_url || '',
        tagline: data.tagline || '',
        accent_color: data.accent_color || '',
        booking_confirmation_text: data.booking_confirmation_text || '',
        cancellation_info: data.cancellation_info || '',
        reply_to_email: data.reply_to_email || '',
        privacy_policy_url: data.privacy_policy_url || '',
        custom_domain: data.custom_domain || '',
      });
    }
  };

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (status) setStatus(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    const payload = {
      therapist_id: therapist.id,
      display_name: form.display_name.trim() || null,
      logo_url: form.logo_url.trim() || null,
      logo_dark_url: form.logo_dark_url.trim() || null,
      icon_url: form.icon_url.trim() || null,
      tagline: form.tagline.trim() || null,
      accent_color: form.accent_color.trim() || null,
      booking_confirmation_text: form.booking_confirmation_text.trim() || null,
      cancellation_info: form.cancellation_info.trim() || null,
      reply_to_email: form.reply_to_email.trim() || null,
      privacy_policy_url: form.privacy_policy_url.trim() || null,
      // custom_domain sätts av Klinikverktyget (kräver DNS + Vercel) — skrivs inte från formuläret.
    };
    const { error } = await supabase
      .from('brand_settings')
      .upsert(payload, { onConflict: 'therapist_id' });
    if (error) {
      setStatus({ type: 'error', message: 'Kunde inte spara: ' + error.message });
    } else {
      setHasExisting(true);
      setStatus({ type: 'success', message: 'Branding sparad!' });
      if (onBrandSaved) onBrandSaved({
        accentColor: form.accent_color.trim() || null,
        logoUrl: form.logo_url.trim() || null,
        displayName: form.display_name.trim() || null,
        tagline: form.tagline.trim() || null,
      });
    }
    setSaving(false);
  };

  const accentForPicker = isValidHex(form.accent_color) ? form.accent_color.trim() : KLINIO_DEFAULT_ACCENT;
  const accent = accentForPicker;
  const accentText = onAccent(accent);
  const fallbackLink = `${window.location.origin}?t=${therapist?.id || ''}`;

  const card = { background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden' };
  const head = { padding: '14px 20px', borderBottom: `1px solid ${theme.card_border}` };
  const h2 = { margin: 0, fontSize: '16px', fontWeight: '700', color: NEUTRAL.ink, fontFamily: 'var(--font-heading)' };
  const body = { padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' };
  const label = { display: 'block', fontSize: '12px', fontWeight: '600', color: NEUTRAL.text, marginBottom: '5px' };
  const hint = { fontSize: '12px', color: NEUTRAL.muted, lineHeight: 1.5 };
  const input = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${theme.card_border}`, fontSize: '14px', color: NEUTRAL.ink, fontFamily: 'inherit', outline: 'none', background: theme.card_bg };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* ── Mörkt huvud med levande förhandsvisning (T-12) ── */}
      <div className="fr-brand-hero" style={{ position: 'relative', overflow: 'hidden', background: NEUTRAL.ink, borderRadius: theme.card_radius, padding: '24px 28px', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '24px' }}>
        <div aria-hidden="true" style={{ position: 'absolute', top: '-150px', right: '200px', width: '380px', height: '380px', borderRadius: '999px', background: accent, opacity: 0.30, filter: 'blur(40px)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: '26px', fontWeight: '800', letterSpacing: '-0.01em', lineHeight: 1.1, fontFamily: 'var(--font-heading)' }}>Varumärke</h1>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', marginTop: '6px', lineHeight: 1.5, maxWidth: '360px' }}>Så ser dina kunder kliniken när de bokar och loggar in. Ändringarna syns direkt i förhandsvisningen.</div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={handleSave} disabled={saving} style={{ ...darkPrimary(theme), background: accent, borderColor: accent, color: accentText, opacity: saving ? 0.7 : 1 }}>{saving ? 'Sparar…' : hasExisting ? 'Spara ändringar' : 'Spara varumärke'}</button>
            {status && <span style={{ fontSize: '12px', color: status.type === 'success' ? theme.accent_soft_bg : '#FCA5A5' }}>{status.message}</span>}
          </div>
        </div>
        {/* Förhandsvisning: kundens startkort */}
        <div className="fr-brand-preview" style={{ position: 'relative', width: '300px', flexShrink: 0, background: '#FFFFFF', borderRadius: '14px', padding: '18px', color: NEUTRAL.ink, boxShadow: '0 10px 30px rgba(0,0,0,0.35)' }}>
          <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: NEUTRAL.faint, marginBottom: '10px' }}>Förhandsvisning · kundens vy</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {form.logo_url?.trim() ? (
              <img src={form.logo_url.trim()} alt="Logotyp" onError={e => { e.currentTarget.style.display = 'none'; }} style={{ height: '36px', maxWidth: '120px', objectFit: 'contain' }} />
            ) : (
              <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: theme.accent_soft_bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontWeight: '800', color: theme.accent_soft_text, fontSize: '16px', flexShrink: 0 }}>
                {(form.display_name || therapist?.business_name || 'K').charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '15px', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.display_name || 'Din klinik'}</div>
              {form.tagline && <div style={{ fontSize: '11px', color: NEUTRAL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.tagline}</div>}
            </div>
          </div>
          <div style={{ width: '100%', boxSizing: 'border-box', marginTop: '14px', background: accent, color: accentText, padding: '10px 12px', borderRadius: theme.btn_radius, fontSize: '13px', fontWeight: '600', textAlign: 'center' }}>Boka tid</div>
          {form.cancellation_info && <div style={{ fontSize: '11px', color: NEUTRAL.muted, marginTop: '10px', lineHeight: 1.5 }}>{form.cancellation_info}</div>}
        </div>
      </div>

      <div className="fr-brand-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px', alignItems: 'start' }}>

        {/* Identitet */}
        <div style={card}>
          <div style={head}><h2 style={h2}>Identitet</h2></div>
          <div style={body}>
            <div>
              <label htmlFor="brand-name" style={label}>Klinikens namn</label>
              <input id="brand-name" type="text" placeholder="T.ex. Rehab Centrum Stockholm" value={form.display_name} onChange={e => handleChange('display_name', e.target.value)} style={input} />
              <div style={{ ...hint, marginTop: '4px' }}>Visas för kunden i stället för plattformens namn.</div>
            </div>
            <div>
              <label htmlFor="brand-tagline" style={label}>Tagline</label>
              <input id="brand-tagline" type="text" placeholder="T.ex. Din partner för återhämtning" value={form.tagline} onChange={e => handleChange('tagline', e.target.value)} style={input} />
            </div>
            <LogoSection theme={theme} therapistId={therapist?.id} logoUrl={form.logo_url} logoDarkUrl={form.logo_dark_url}
              onChange={({ logo_url, logo_dark_url }) => { handleChange('logo_url', logo_url); handleChange('logo_dark_url', logo_dark_url); }} />
          </div>
        </div>

        {/* Accentfärg */}
        <div style={card}>
          <div style={head}><h2 style={h2}>Accentfärg</h2></div>
          <div style={body}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {PRESET_COLORS.map(color => {
                const sel = form.accent_color.trim().toLowerCase() === color.toLowerCase();
                return (
                  <button key={color} type="button" title={color} aria-label={color} onClick={() => handleChange('accent_color', color)}
                    style={{ width: '36px', height: '36px', borderRadius: '999px', background: color, border: `3px solid ${sel ? NEUTRAL.ink : '#FFFFFF'}`, boxShadow: `0 0 0 1px ${theme.card_border}`, cursor: 'pointer', padding: 0, boxSizing: 'border-box' }} />
                );
              })}
              <button type="button" onClick={() => colorInputRef.current?.click()} title="Välj valfri färg" aria-label="Välj valfri färg"
                style={{ width: '36px', height: '36px', borderRadius: '999px', background: 'conic-gradient(#f97316, #ec4899, #8b5cf6, #06b6d4, #16a34a, #f97316)', border: '3px solid #FFFFFF', boxShadow: `0 0 0 1px ${theme.card_border}`, cursor: 'pointer', padding: 0, boxSizing: 'border-box', position: 'relative' }}>
                <input ref={colorInputRef} type="color" value={accentForPicker} onChange={e => handleChange('accent_color', e.target.value)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }} tabIndex={-1} />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <label htmlFor="brand-hex" style={{ ...label, marginBottom: 0 }}>Egen färg</label>
              <input id="brand-hex" type="text" placeholder="#2C513F" value={form.accent_color} onChange={e => handleChange('accent_color', e.target.value)} style={{ ...input, width: '110px', fontFamily: 'monospace', fontSize: '13px' }} />
              {isValidHex(form.accent_color) && (
                <span style={{ fontSize: '12px', color: NEUTRAL.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '7px', height: '7px', borderRadius: '999px', background: STATUS_COLORS.success }} />
                  {accentText === '#FFFFFF' ? 'Vit text läsbar' : 'Mörk text används'}
                </span>
              )}
            </div>
            <div style={hint}>Påverkar knappar och markeringar i kundens vy. På ljusa färger får knapparna automatiskt mörk text så att de går att läsa.</div>
          </div>
        </div>

        {/* Appikon (valfri) */}
        <div style={card}>
          <div style={head}><h2 style={h2}>Appikon <span style={{ fontWeight: '600', color: NEUTRAL.muted, fontSize: '13px' }}>· valfri</span></h2>
            <div style={{ ...hint, marginTop: '3px' }}>Den lilla bilden på kundens hemskärm och i webbläsarfliken.</div></div>
          <div style={body}>
            <IconSection theme={theme} therapistId={therapist?.id} iconUrl={form.icon_url} name={form.display_name || therapist?.business_name} onChange={url => handleChange('icon_url', url)} />
          </div>
        </div>

        {/* Texter till kunden */}
        <div style={card}>
          <div style={head}><h2 style={h2}>Texter till kunden</h2></div>
          <div style={body}>
            <div>
              <label htmlFor="brand-cancel" style={label}>Avbokningsinfo <span style={{ fontWeight: '400', color: NEUTRAL.muted }}>· visas när kunden bokar</span></label>
              <textarea id="brand-cancel" rows={2} placeholder="T.ex. Avboka senast 24 timmar innan." value={form.cancellation_info} onChange={e => handleChange('cancellation_info', e.target.value)} style={{ ...input, resize: 'vertical' }} />
              <div style={{ ...hint, marginTop: '4px' }}>Bara information – appen spärrar inga avbokningar.</div>
            </div>
            <div>
              <label htmlFor="brand-confirm" style={label}>Bokningsbekräftelse <span style={{ fontWeight: '400', color: NEUTRAL.muted }}>· i mejlet</span></label>
              <textarea id="brand-confirm" rows={5} placeholder={`Hej {kundnamn},\n\nVi bekräftar din bokning. Vi ser fram emot att träffa dig!`} value={form.booking_confirmation_text} onChange={e => handleChange('booking_confirmation_text', e.target.value)} style={{ ...input, resize: 'vertical', lineHeight: 1.6 }} />
              <div style={{ ...hint, marginTop: '4px' }}>
                Datum, tid och tjänst visas alltid automatiskt. Platshållare:{' '}
                {['{kundnamn}', '{datum}', '{tid}', '{tjänst}', '{klinik}'].map(v => (
                  <code key={v} style={{ background: theme.page_bg, padding: '1px 5px', borderRadius: '4px', fontFamily: 'monospace', marginRight: '4px' }}>{v}</code>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Adress & kontakt */}
        <div style={card}>
          <div style={head}><h2 style={h2}>Adress &amp; kontakt</h2></div>
          <div style={body}>
            <div>
              <div style={label}>Kundernas adress</div>
              {form.custom_domain ? (
                <CopyRow theme={theme} value={`https://${form.custom_domain}`} display={form.custom_domain} status="Aktiv" />
              ) : (
                <div style={{ ...hint }}>Ingen egen adress ännu (t.ex. boka.dinklinik.se). Kopplas av Klinikverktyget — med egen adress ser kunden bara ditt varumärke, även vid inloggning.</div>
              )}
              <div style={{ ...hint, marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span>{form.custom_domain ? 'Reservlänk:' : 'Din länk till kunder:'}</span>
                <CopyRow theme={theme} value={fallbackLink} display={fallbackLink.replace(/^https?:\/\//, '')} compact />
              </div>
            </div>
            <div>
              <label htmlFor="brand-reply" style={label}>Svarsadress för mejl</label>
              <input id="brand-reply" type="email" placeholder="din@klinik.se" value={form.reply_to_email} onChange={e => handleChange('reply_to_email', e.target.value)} style={input} />
              <div style={{ ...hint, marginTop: '4px' }}>Dit svarar kunden på bekräftelsemejlet. Tomt = din inloggningsadress.</div>
            </div>
            <div>
              <label htmlFor="brand-policy" style={label}>Integritetspolicy (länk)</label>
              <input id="brand-policy" type="url" placeholder="https://dinklinik.se/integritetspolicy/" value={form.privacy_policy_url} onChange={e => handleChange('privacy_policy_url', e.target.value)} style={input} />
              <div style={{ ...hint, marginTop: '4px' }}>Länkas när en ny kund aktiverar sitt konto.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CopyRow({ theme, value, display, status, compact }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  if (compact) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
        <span style={{ fontFamily: 'monospace', fontSize: '11px', color: NEUTRAL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }}>{display}</span>
        <button type="button" onClick={copy} style={{ background: 'none', border: 'none', color: theme.accent_text, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>{copied ? 'Kopierad!' : 'Kopiera'}</button>
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', border: `1px solid ${theme.card_border}`, borderRadius: '8px', padding: '9px 12px', background: theme.page_bg }}>
      <span style={{ flex: 1, minWidth: 0, fontSize: '13px', color: NEUTRAL.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
      {status && <span style={{ fontSize: '11px', fontWeight: '600', color: '#15803D', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}><span style={{ width: '7px', height: '7px', borderRadius: '999px', background: STATUS_COLORS.success }} />{status}</span>}
      <button type="button" onClick={copy} style={{ background: 'transparent', border: `1px solid ${theme.card_border}`, color: NEUTRAL.text, padding: '5px 10px', borderRadius: theme.btn_radius, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>{copied ? 'Kopierad!' : 'Kopiera'}</button>
    </div>
  );
}

export default BrandAdmin;
